import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ArrowRight, Mail, ShieldAlert } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Every sign-in email this user has had, newest first.
 *
 * Read straight from admin_audit_log rather than a side table: the row is
 * written by admin_change_user_email() inside the same transaction as the
 * change itself, so an actor cannot perform the change and then decline to log
 * it, and a rolled-back change leaves no misleading line either. That makes
 * this the authoritative history, not a best-effort copy of one.
 *
 * admin_audit_log has no UPDATE or DELETE policy (see creator-app
 * 20260610000000_security_hardening_rls_fixes.sql) and reads are restricted to
 * is_admin(), so these rows cannot be edited away and are not visible to the
 * brands who share this Supabase project.
 */

export interface EmailChangeLogRow {
  id: string;
  created_at: string;
  admin_email: string | null;
  old_values: Record<string, string> | null;
  new_values: Record<string, string> | null;
  metadata: {
    action?: string;
    reason?: string | null;
    touched?: Record<string, number>;
    trolley_override?: boolean;
    oauth_override?: boolean;
  } | null;
}

export type IdentityKind = 'email' | 'handle';

const KIND_META: Record<IdentityKind, { action: string; table: string; field: string; label: string }> = {
  email:  { action: 'users.change_email',         table: 'auth.users',             field: 'email',         label: 'email changes' },
  handle: { action: 'users.change_tiktok_handle', table: 'profiles.tiktok_handle', field: 'tiktok_handle', label: 'username changes' },
};

export function useUserEmailHistory(userId: string | null | undefined, kind: IdentityKind = 'email') {
  const meta = KIND_META[kind];
  return useQuery<EmailChangeLogRow[]>({
    queryKey: ['user-email-history', userId, kind],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_audit_log')
        .select('id, created_at, admin_email, old_values, new_values, metadata')
        .eq('table_name', meta.table)
        .eq('record_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      // table_name='auth.users' is only written by the email-change RPC today,
      // but filter on the action marker anyway so a future writer on the same
      // table can't quietly start appearing in here as an "email change".
      return ((data ?? []) as EmailChangeLogRow[]).filter(
        r => r.metadata?.action === meta.action,
      );
    },
  });
}

export function UserEmailHistory({ userId, compact = false, kind = 'email' }: {
  userId: string | null | undefined; compact?: boolean; kind?: IdentityKind;
}) {
  const meta = KIND_META[kind];
  const { data: rows, isLoading, error } = useUserEmailHistory(userId, kind);
  const val = (v: Record<string, string> | null | undefined) =>
    (v?.[meta.field] ?? 'unknown');

  if (isLoading) {
    return <div className="space-y-2">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  }

  if (error) {
    return <p className="text-xs text-destructive">Couldn&apos;t load the email history.</p>;
  }

  if (!rows || rows.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        {compact ? `No previous ${meta.label} on this account.` : `This account has never had its ${kind === 'email' ? 'sign-in email' : 'TikTok username'} changed.`}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {/* Repeated changes on one account are a fraud signal, not a statistic —
          say it rather than making someone count the rows. */}
      {compact && rows.length >= 2 && (
        <div className="flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs font-medium text-amber-900">
          <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
          This account&apos;s {kind === 'email' ? 'email' : 'username'} has already been changed {rows.length} times.
        </div>
      )}

      {rows.map(r => (
        <div key={r.id} className="rounded-md border bg-muted/30 p-2.5 text-xs">
          <div className="flex flex-wrap items-center gap-1.5">
            <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="font-mono line-through decoration-muted-foreground/60">
              {val(r.old_values)}
            </span>
            <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="font-mono font-medium">{val(r.new_values)}</span>
            {r.metadata?.trolley_override && (
              <Badge variant="outline" className="border-amber-400 text-[10px] text-amber-700">
                Trolley override
              </Badge>
            )}
          </div>

          <div className="mt-1 text-muted-foreground">
            {format(new Date(r.created_at), 'MMM d, yyyy · h:mm a')}
            {r.admin_email && <> · by <span className="font-medium text-foreground">{r.admin_email}</span></>}
          </div>

          {r.metadata?.reason && (
            <p className="mt-1 border-l-2 border-muted-foreground/30 pl-2 italic text-muted-foreground">
              {r.metadata.reason}
            </p>
          )}

          {/* What the change actually moved and revoked. Kept behind a details
              element: it matters when reconstructing an incident, and is noise
              the rest of the time. */}
          {r.metadata?.touched && Object.keys(r.metadata.touched).length > 0 && (
            <details className="mt-1.5">
              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                What it touched
              </summary>
              <ul className="mt-1 space-y-0.5 pl-3 text-muted-foreground">
                {Object.entries(r.metadata.touched).map(([k, v]) => (
                  <li key={k}>
                    <span className="font-mono">{k}</span>: {v}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      ))}
    </div>
  );
}
